import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { JSONRPCError, Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import { Feature, UserModalComponent, UserModalConfig, UserModalUpdate } from 'src/app/components/user-modal/user-modal.component';
import { UserMultiModalComponent, UserMultiModalResult } from 'src/app/components/user-multi-modal/user-multi-modal.component';
import { Columns } from 'src/app/components/user-page-view/user-page-view.component';
import { AlertService } from 'src/app/services/alert.service';
import { CmsService } from 'src/app/services/cms.service';
import { CsvService } from 'src/app/services/csv.service';
import { DurationService } from 'src/app/services/duration.service';
import { Role, Template } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-whitelist',
  templateUrl: './whitelist.component.html',
  styleUrls: ['./whitelist.component.scss']
})
export class WhitelistComponent implements OnInit, OnDestroy {
  public entries: Template[] = [];
  public columns: Columns = {
    uuid: false,
    username: true,
    fullname: true,
    mail: true,
    labels: false,
    roles: true,
    registered: null,
    created: false,
    expiry: true
  };
  public clearSelection = new Subject<void>();
  public clearSelectionChange: Observable<void>;

  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Delete));
  }

  public get roles(): BehaviorSubject<Role[]> {
    return this.users.roles;
  }

  private subscription: Subscription;

  constructor(
    private alert: AlertService,
    private modal: NgbModal,
    private toastr: ToastrService,
    private users: UserService,
    private cms: CmsService,
    private csv: CsvService,
    private duration: DurationService
  ) {}

  public ngOnInit() {
    this.clearSelectionChange = this.clearSelection.asObservable();
    this.subscription = this.users.templates.subscribe(entries => {
      this.entries = entries.filter(e => e.key == null);
      this.columns.username = this.entries.some(e => e.username != null) ? true : null;
      this.columns.pattern = this.entries.some(e => e.pattern != null) || this.entries.length == 0 ? true : null;
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public copy(item) {
    return JSON.parse(JSON.stringify(item));
  }

  public openCreateModal(pattern: boolean) {
    const modalRef = this.modal.open(UserModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef);
    modalRef.componentInstance.config = this.modalConfig(false, false, pattern);
    modalRef.componentInstance.onSubmit.subscribe((data: UserModalUpdate) => {
      this.create(data.model);
    });
  }

  public openUpdateModal(template: Template) {
    const modalRef = this.modal.open(UserModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef);
    const pattern = template.pattern != null && template.pattern !== '';
    modalRef.componentInstance.config = this.modalConfig(true, false, pattern); // template.key != null -> false
    modalRef.componentInstance.model = this.copy(template);

    modalRef.componentInstance.onSubmit.subscribe((data: UserModalUpdate) => {
      Object.assign(template, data.model);
      this.update(template);
    });
  }

  public openUpdateMultiModal(templates: Template[]) {
    const modalRef = this.modal.open(UserMultiModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef, () => this.clearSelection.next());
    modalRef.componentInstance.config = {
      title: `Update ${templates.length} Entries`,
      state: true,
      duration: true
    };
    modalRef.componentInstance.onSubmit.subscribe((res: UserMultiModalResult) => {
      return Promise.all(
        templates.map(e => {
          if (res.toggleEnabled) e.profile.enabled = res.enabled;
          if (res.toggleRoles) e.profile.meta.roles = res.roles;
          if (res.toggleExpiration) e.profile.meta.expiration = res.expiration;
          if (res.toggleDuration) e.profile.meta.duration = res.duration;
          return this.update(e);
        })
      );
    });
  }

  // Pipe information about all available roles into the modal.
  // Optionally perform the cleanup command IF the modal was closed successfully.
  private putRolesAndCleanUp(modalRef: NgbModalRef, cleanup: () => void = null) {
    const rolesSub = this.roles.subscribe(r => (modalRef.componentInstance.roles = r));
    modalRef.result.then(
      () => {
        rolesSub?.unsubscribe();
        if (cleanup) cleanup();
      },
      () => rolesSub?.unsubscribe()
    );
  }

  private modalConfig(update: boolean, forceLicence: boolean, pattern: boolean): UserModalConfig {
    const licence = !pattern || forceLicence ? Feature.optional : null;
    const header = pattern || forceLicence ? 'Template' : 'Whitelist';
    return { header, update, state: false, settings: true, template: true, licence, pattern };
  }

  public reload(): Promise<any> {
    this.entries = [];
    return this.users.loadTemplates();
  }

  public create(template: Template): Promise<any> {
    if (
      this.entries.find(
        entry => (entry.username && entry.username === template.username) || (entry.pattern && entry.pattern === template.pattern)
      )
    ) {
      return this.alert.fireError('Already exists', 'A profile or whitelist entry with the same username or mail already exists.');
    }
    return this.users
      .createTemplate(template)
      .then(() => this.toastr.success(`Whitelist "${this.str(template)}" created`))
      .catch(err => {
        if (err instanceof JSONRPCError && err.code == 3011) {
          return this.alert.fireError('Already exists', 'A profile or whitelist entry with the same username or mail already exists.');
        }
        throw err;
      });
  }

  public update(template: Template): Promise<any> {
    if (
      this.entries.find(
        entry =>
          entry.uuid !== template.uuid &&
          ((entry.username && entry.username === template.username) || (entry.pattern && entry.pattern === template.pattern))
      )
    ) {
      return this.alert.fireError('Already exists', 'A profile or whitelist entry with the same username or mail already exists.');
    }

    return this.users
      .updateTemplate(template)
      .then(() => this.toastr.success(`Whitelist "${this.str(template)}" updated`))
      .catch(err => {
        if (err instanceof JSONRPCError && err.code == 3011) {
          return this.alert.fireError('Already exists', 'A profile or whitelist entry with the same username or mail already exists.');
        }
        throw err;
      });
  }

  public delete(template: Template): Promise<any> {
    return this.users.relatedLicencesForDeletion(template.username).then(licences => {
      if (licences != null) {
        return this.users
          .deleteTemplate(template)
          .then(() => this.toastr.success(`Whitelist "${this.str(template)}" deleted`))
          .then(() => Promise.all(licences.map(l => this.deleteLicence(l))));
      }
    });
  }

  private deleteLicence(template: Template): Promise<any> {
    return this.users.deleteTemplate(template).then(() => this.toastr.success(`Whitelist "${template.pattern}" deleted`));
  }

  public deleteMulti(templates: Template[]): Promise<any> {
    return Promise.all(templates.map(e => this.delete(e)));
  }

  public async exportToCsv(): Promise<void> {
    try {
      // The user server deletes a whitelist entry once its user registers, so reload to avoid exporting registered users.
      await this.users.loadTemplates();
    } catch (err: any) {
      this.alert.fireError('Export Failed', err?.message || 'Failed to load the whitelist');
      return;
    }

    // Single user entries only; entries with a pattern are group templates (same distinction as openUpdateModal).
    const rows = this.entries
      .filter(entry => entry.pattern == null || entry.pattern === '')
      .map(entry => {
        const meta = entry.profile?.meta;
        const settings = entry.profile?.settings;
        return {
          Username: entry.username,
          'Full Name': [settings?.title, settings?.firstname, settings?.lastname].filter(s => s?.length).join(' '),
          Email: meta?.mail,
          Roles: (meta?.roles ?? []).join('; '),
          Licences: (meta?.licences ?? [])
            .map(l => l.key)
            .filter(k => k)
            .join('; '),
          Enabled: entry.profile?.enabled ? 'Yes' : 'No',
          Expiration: meta?.duration ? this.duration.formatIso(meta.duration) : meta?.expiration ? new Date(meta.expiration) : '',
          Created: entry.created ? new Date(entry.created) : ''
        };
      });

    if (rows.length === 0) {
      this.toastr.info('There are no whitelisted users to export.');
      return;
    }

    this.csv.export(`whitelist-users-${new Date().toISOString().split('T')[0]}.csv`, rows);
  }

  private str(template: Template): string {
    return (template.username || template.pattern) + (template.pattern ? ' (' + template.pattern + ')' : '');
  }
}
